import { ResourceType } from 'src/utils/resource-types'
import { Arn } from 'src/utils/types'
import {
  AcmCertificateNotFoundIssue,
  AmiStorageNotAccessibleIssue,
  BaseIssue,
  BucketBackfillFailedIssue,
  ClientErrorIssue,
  DrsAgentNotFoundIssue,
  FailbackResourceIssue,
  KeyPairNotFoundIssue,
  MarketplaceActionRequiredIssue,
  MarketplaceAmiCantBeCopiedIssue,
  MarketplaceInstanceCantBeCopiedIssue,
  PersistentIssue,
  QuotaIssue,
  RaisedIssueScope,
  RefreshClientErrorIssue,
  S3BucketReplicationAlreadyConfiguredIssue,
  S3BucketReplicationNotConfiguredIssue,
  SubscriptionOverageIssue,
  SyncPairPermissionIssue,
  UnconfiguredS3BucketReplicationIssue,
  UnhandledRouteTargetIssue,
  UninstallDrsAgentIssue,
} from '../account/services/issue'
import {
  adaptApplicationIssueDto,
  adaptSyncPairIssueDto,
  ApplicationIssueDto,
  ApplicationIssueScopeDto,
  SyncPairIssueScopeDto,
} from '../services/dtos'

let baseIssue = {
  type: 'issue',
  message: 'This is a basic issue and this is its message.',
}
let persistentIssue = {
  type: 'persistentIssue',
  message: 'This is a persistent issue.  It should require acknowledgement.',
}
let acmCertNotFoundIssue = {
  type: 'acmCertificateNotFound',
  message: "Couldn't find the cert!",
  sourceCertificateArn: 'arn:aws:acm:region:account:cert',
  domainName: 'missing.cert.com',
}
let clientErrorIssue = {
  type: 'clientError',
  message: 'ClientError occurred!',
  errorCode: 'SomeErrorCode',
  errorMessage: 'Some Error Occurred',
  operation: 'SomeOperation',
}
let quotaIssue = {
  type: 'quota',
  message: 'Hit a limit!',
  errorCode: 'LimitExceeded',
  errorMessage: 'You hit your limit.',
  operation: 'LimitedOperation',
}
let marketplaceAmiCantBeCopiedIssue = {
  type: 'marketplaceAmiCantBeCopied',
  message: "Can't copy this marketplace image",
  productCodes: ['product-code-123'],
}
let marketplaceInstanceCantBeCopiedIssue = {
  type: 'marketplaceInstanceCantBeCopied',
  message: "Can't copy this instance",
}
let amiStorageNotAccessibleIssue = {
  type: 'amiStorageNotAccessible',
  message: "Can't access the storage here.",
}
let unhandledRouteTargetIssue = {
  type: 'unhandledRouteTarget',
  message: "Can't handle this route target!",
  routeTableArn: 'arn:aws:ec2:region:account:route-table/12345',
  target: 'target',
}
let marketplaceActionRequiredIssues = {
  type: 'marketplaceActionRequired',
  message: 'You need to buy something.',
  marketplaceUrl: 'https://marketplace.url.com',
}
let refreshClientErrorIssue = {
  type: 'refreshClientError',
  message: 'Failed to refresh',
  errorCode: 'ErrorCode',
  errorMessage: 'Something is broken.',
  operation: 'SomeOperation',
  resourceType: 'ec2Instance',
}
let keyPairNotFoundIssue = {
  type: 'keyPairNotFound',
  keyPairName: 'key-pair-name',
}
let s3BucketReplAlreadyConfiguredIssue = {
  type: 's3BucketReplicationAlreadyConfigured',
  bucketName: 'bucket-name',
}
let unconfiguredS3BucketReplIssue = {
  type: 'unconfiguredS3BucketReplication',
  bucketName: 'bucket-name',
  message: 'you gotta configure this guy',
}
let bucketBackfillFailedIssue = {
  type: 'bucketBackfillFailed',
  failedBucketReports: ['failed-backfill-report'],
  totalCopies: 1,
  failedCopies: 1,
  message: 'Backfill no workie.',
}
let s3BucketReplicationNotConfiguredIssue = {
  type: 's3BucketReplicationNotConfigured',
  sourceBucketName: 'bucket-name',
  replicationRoleArn: 'arn:aws:iam::account:role/role-name',
  targetBucketArn: 'arn:aws:s3:::target-bucket',
  kmsKeyArn: 'arn:aws:kms:region:account:key-123',
  message: "You're gonna wann configure this yourself.",
}
let uninstallDrsAgentIssue = {
  type: 'drsUninstallAgent',
  sourceServerId: 'source-server-id',
  ec2InstanceId: 'i-1234567890',
}
let failbackResourceIssue = {
  type: 'failbackResource',
  message: 'Something broke in failback for this guy.',
  resourceArn: 'arn:aws:svc:region:account:resource/12345',
  resourceId: 'resource-id',
  resourceType: 'ec2Instance',
}
let syncPairPermissionIssue = {
  type: 'permission',
  message: "You don't have permission.",
  operation: 'SomeOperation',
}
let syncPairIamPermissionIssue = {
  type: 'permission',
  message: 'You didn\t grant Arpio IAM perms',
  operation: 'CreateRole',
}
let drsAgentNotFoundIssue = {
  type: 'drsReplicationAgentNotFound',
  message: "Couldn't find the DRS agent.",
  hardwareId: 'hardware-id',
  usingSnapshots: true,
}
let subscriptionOverageIssue = {
  type: 'entitlementLimitsExceeded',
  message: "You're over the limit.",
}
let eksClusterAccessDeniedIssue = {
  type: 'eksClusterAccessDenied',
  message: "Arpio can't access your cluster.",
  clusterName: 'your-cluster',
  delegateRoleArn: 'arn:aws:iam::account:role/ArpioDelegateRole',
  region: 'us-east-3',
}

let applicationIssueScopeMapping = new Map<string, ApplicationIssueScopeDto>([
  ['backup', ApplicationIssueScopeDto.BACKUP],
  ['restore', ApplicationIssueScopeDto.RESTORE],
  ['cleanup', ApplicationIssueScopeDto.CLEANUP],
  ['failback', ApplicationIssueScopeDto.FAILBACK],
])

export function getMockApplicationIssueDtos() {
  return getMockIssues(applicationIssueScopeMapping).map(adaptApplicationIssueDto)
}

let syncPairIssueScopeMapping = new Map<string, SyncPairIssueScopeDto>([
  ['backup', SyncPairIssueScopeDto.STAGING],
  ['restore', SyncPairIssueScopeDto.SYNC],
  ['cleanup', SyncPairIssueScopeDto.CLEANUP],
  ['failback', SyncPairIssueScopeDto.FAILBACK],
])

export function getMockSyncPairIssueDtos() {
  return getMockIssues(syncPairIssueScopeMapping).map(adaptSyncPairIssueDto)
}

function getMockIssues(scopeMapping: Map<string, ApplicationIssueScopeDto | SyncPairIssueScopeDto>) {
  return [
    {
      issueId: 'baseIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: baseIssue,
      scope: scopeMapping.get('backup'),
    },
    {
      issueId: 'baseIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: baseIssue,
      scope: scopeMapping.get('restore'),
    },
    {
      issueId: 'baseIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: baseIssue,
      scope: scopeMapping.get('cleanup'),
    },
    {
      issueId: 'baseIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: baseIssue,
      scope: scopeMapping.get('failback'),
    },
    {
      issueId: 'persistentIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: persistentIssue,
      scope: scopeMapping.get('backup'),
    },
    {
      issueId: 'acmCertNotFoundIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: acmCertNotFoundIssue,
      scope: scopeMapping.get('restore'),
    },
    {
      issueId: 'clientErrorIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: clientErrorIssue,
      scope: scopeMapping.get('restore'),
    },
    {
      issueId: 'quotaIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: quotaIssue,
      scope: scopeMapping.get('restore'),
    },
    {
      issueId: 'marketplaceAmiCantBeCopiedIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: marketplaceAmiCantBeCopiedIssue,
      scope: scopeMapping.get('restore'),
    },
    {
      issueId: 'marketplaceInstanceCantBeCopiedIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: marketplaceInstanceCantBeCopiedIssue,
      scope: scopeMapping.get('restore'),
    },
    {
      issueId: 'amiStorageNotAccessibleIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: amiStorageNotAccessibleIssue,
      scope: scopeMapping.get('restore'),
    },
    {
      issueId: 'unhandledRouteTargetIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: unhandledRouteTargetIssue,
      scope: scopeMapping.get('restore'),
    },
    {
      issueId: 'marketplaceActionRequiredIssues',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: marketplaceActionRequiredIssues,
      scope: scopeMapping.get('restore'),
    },
    {
      issueId: 'refreshClientErrorIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: refreshClientErrorIssue,
      scope: scopeMapping.get('backup'),
    },
    {
      issueId: 'keyPairNotFoundIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: keyPairNotFoundIssue,
      scope: scopeMapping.get('backup'),
    },
    {
      issueId: 's3BucketReplAlreadyConfiguredIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: s3BucketReplAlreadyConfiguredIssue,
      scope: scopeMapping.get('backup'),
    },
    {
      issueId: 'unconfiguredS3BucketReplIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: unconfiguredS3BucketReplIssue,
      scope: scopeMapping.get('backup'),
    },
    {
      issueId: 'bucketBackfillFailedIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: bucketBackfillFailedIssue,
      scope: scopeMapping.get('backup'),
    },
    {
      issueId: 's3BucketReplicationNotConfiguredIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: s3BucketReplicationNotConfiguredIssue,
      scope: scopeMapping.get('backup'),
    },
    {
      issueId: 'uninstallDrsAgentIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: uninstallDrsAgentIssue,
      scope: scopeMapping.get('cleanup'),
    },
    {
      issueId: 'failbackResourceIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: failbackResourceIssue,
      scope: scopeMapping.get('failback'),
    },
    {
      issueId: 'syncPairPermissionIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: syncPairPermissionIssue,
      scope: scopeMapping.get('restore'),
    },
    {
      issueId: 'syncPairIamPermissionIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: syncPairIamPermissionIssue,
      scope: scopeMapping.get('restore'),
    },
    {
      issueId: 'drsAgentNotFoundIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: drsAgentNotFoundIssue,
      scope: scopeMapping.get('backup'),
    },
    {
      issueId: 'subscriptionOverageIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: subscriptionOverageIssue,
      scope: scopeMapping.get('backup'),
    },
    {
      issueId: 'eksClusterAccessDeniedIssue',
      arn: 'arn:....' as Arn,
      timestamp: new Date().toISOString(),
      issue: eksClusterAccessDeniedIssue,
      scope: scopeMapping.get('backup'),
    },
  ]
}
